import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { dataService } from '@/services/dataService';
import { AppNotification } from '@/types/entities';

/**
 * Real local notifications, derived from demo data (follow-ups due,
 * approvals waiting, at-risk creators, expiring contracts) — not a fake
 * feed. Mark read/unread and clear are real local mutations. What this
 * never claims: email/push/mobile delivery — there is no channel for
 * that without a backend, so the copy says so explicitly.
 */
export function NotificationCenter() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<AppNotification[]>([]);

  function reload() {
    dataService.listNotifications().then(setNotes);
  }
  useEffect(reload, []);
  useEffect(() => {
    if (open) reload();
  }, [open]);

  const unreadCount = notes.filter((n) => !n.read).length;

  return (
    <div className="popover-anchor">
      <button className="icon-btn" onClick={() => setOpen((o) => !o)} aria-label={t('topbar.notifications')}>
        🔔{unreadCount > 0 && <span className="dot-badge">{unreadCount}</span>}
      </button>
      {open && (
        <>
          <div className="popover-backdrop" onClick={() => setOpen(false)} />
          <div className="popover-panel" style={{ width: 320 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="popover-title">{t('notifications.title')}</div>
              {notes.length > 0 && (
                <button style={{ fontSize: 11 }} onClick={async () => { await dataService.markAllNotificationsRead(); reload(); }}>{t('notifications.markAllRead')}</button>
              )}
            </div>
            {notes.length === 0 ? (
              <div className="popover-empty">{t('notifications.empty')}</div>
            ) : (
              <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                {notes.map((n) => (
                  <div key={n.id} className={`notif-item ${n.read ? 'read' : ''}`}>
                    <div
                      style={{ flex: 1, cursor: n.entityRoute ? 'pointer' : 'default' }}
                      onClick={async () => {
                        await dataService.markNotificationRead(n.id, true);
                        if (n.entityRoute) { navigate(n.entityRoute); setOpen(false); }
                        reload();
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: n.read ? 400 : 700 }}>{n.title}</div>
                      {n.detail && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{n.detail}</div>}
                    </div>
                    <button
                      className="icon-btn"
                      style={{ width: 22, height: 22, marginInlineStart: 4 }}
                      title={t('notifications.clear')}
                      onClick={async (e) => { e.stopPropagation(); await dataService.clearNotification(n.id); reload(); }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 10, color: 'var(--text-3)', padding: '8px 8px 2px' }}>{t('notifications.noRemoteDelivery')}</div>
          </div>
        </>
      )}
    </div>
  );
}
